// 🕹️🦉 MikoArcadeBeatEmUp — coin-op arcade shell for Miko's story.
//
// Canon (owner-locked Feb/Aug 2026):
//   🌀 Glitch was Miko's attacker AND her savior.
//   👁️ The Chronophage cascaded her into the cabinet's code.
//   🦉 Miko is a HIDDEN, OPTIONAL encounter across the stages — not a boss.
//   👁️ Chronophage is the boss (with a secret "Gail" swap).
//   🪙 The cabinet is coin-op — insert Phoenix Tokens as arcade coins.
//
// This file holds the SHELL only. Stages, hero specials, the Chronophage
// kit, and the hidden-Miko trigger are all OWNER-DROP zones.
// When the backend catalog reports `spec_status: OWNER_DROP_PENDING` or
// zero stages, this component renders the "AWAITING OWNER DROP" screen
// instead of live gameplay.

import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Gamepad2, Coins, Lock, PlayCircle, CheckCircle2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import MikoArcadeCanvas from "./MikoArcadeCanvas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MikoArcadeBeatEmUp() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [selectedHero, setSelectedHero] = useState(null);
  const [inserting, setInserting] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [sessionPayload, setSessionPayload] = useState(null); // full session data for canvas
  const [reward, setReward] = useState(null);
  const [ending, setEnding] = useState(false);

  const refreshActive = async () => {
    try {
      const r = await axios.get(`${API}/miko-arcade/session/active`);
      if (r.data?.session) {
        setActiveSession(r.data.session);
        setSelectedHero(r.data.session.hero_class);
      } else {
        setActiveSession(null);
      }
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [cat, active] = await Promise.all([
          axios.get(`${API}/miko-arcade/catalog`),
          axios.get(`${API}/miko-arcade/session/active`).catch(() => ({ data: { session: null } })),
        ]);
        if (!alive) return;
        setCatalog(cat.data);
        if (active.data?.session) {
          setActiveSession(active.data.session);
          setSelectedHero(active.data.session.hero_class);
        }
      } catch (e) {
        /* silent — the AWAITING screen handles null */
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!user) {
    return (
      <div className="text-center py-16 text-neutral-400" data-testid="miko-arcade-signed-out">
        Sign in to play Miko&apos;s Arcade.
      </div>
    );
  }
  if (!catalog) {
    return (
      <div className="text-center py-16 text-neutral-400">
        <Loader2 className="animate-spin inline mr-2" size={16} /> Loading the cabinet…
      </div>
    );
  }

  const stages = catalog.stages || [];
  const roster = catalog.launch_roster || [];
  const coinOp = catalog.coin_op || {};
  const housing = catalog.arcade_housing || {};
  const miko   = catalog.miko_area_boss || catalog.miko_hidden || {};
  const stagesReady = stages.length > 0;

  // ─── If we have a full session payload, render the playable canvas ───
  if (sessionPayload && !reward) {
    return (
      <div data-testid="miko-arcade-shell-playing" className="text-white">
        <div className="border-2 border-[#D4AF37] bg-[#0A0A0A] p-3 mb-4 flex items-center justify-between">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8]">
            <Gamepad2 size={10} className="inline mr-2" />
            Pocket World Live · {roster.find((h) => h.class_id === selectedHero)?.display}
          </div>
          <button
            data-testid="arcade-forfeit-btn"
            disabled={ending}
            onClick={async () => {
              setEnding(true);
              try {
                const r = await axios.post(`${API}/miko-arcade/session/end`, {
                  session_id: sessionPayload.session_id,
                  victory: false, score: 0, time_sec: 5, hits_landed: 0,
                });
                setReward(r.data);
              } catch (e) { toast.error("Could not end session"); }
              finally { setEnding(false); setSessionPayload(null); setActiveSession(null); }
            }}
            className="text-[10px] uppercase tracking-widest border border-neutral-700 px-2 py-1 text-neutral-400 hover:text-white hover:border-white"
          >
            <LogOut size={10} className="inline mr-1" /> Exit
          </button>
        </div>
        <MikoArcadeCanvas
          session={sessionPayload}
          onExit={async (result) => {
            if (ending) return;
            setEnding(true);
            try {
              const r = await axios.post(`${API}/miko-arcade/session/end`, {
                session_id: result.session_id,
                victory: !!result.victory,
                score: Math.max(0, Math.floor(result.score)),
                time_sec: Math.max(0, result.time_sec),
                hits_landed: Math.max(0, Math.floor(result.hits_landed)),
              });
              setReward({ ...r.data, victory: result.victory });
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Reward payout failed.");
            } finally {
              setEnding(false);
              setSessionPayload(null);
              setActiveSession(null);
            }
          }}
        />
      </div>
    );
  }

  // ─── If we just resolved a run, show the reward screen ───
  if (reward) {
    return (
      <div data-testid="miko-arcade-reward" className="text-white">
        <div className="border-2 border-[#D4AF37] bg-[#0A0A0A] p-6 text-center">
          <div className="font-display text-3xl md:text-5xl text-[#FACC15] mb-2">
            {reward.victory ? "STAGE 1 CLEAR!" : "GAME OVER"}
          </div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-4">
            {reward.victory
              ? "Pocket world cleared · Miko-blueprint drops incoming"
              : "The pocket world remains. Try again."}
          </div>
          {reward.reward && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-left text-xs">
              {reward.reward.first_clear && (
                <div className="border border-[#38BDF8]/50 bg-[#38BDF8]/5 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#38BDF8]">First Clear</div>
                  <div className="font-display text-lg mt-1">Miko un-trapped 🦉</div>
                  <div className="text-neutral-400 text-[11px] mt-1">
                    Playable in the RPG. Full dossier lore unlocked.
                  </div>
                </div>
              )}
              {reward.reward.life_ash != null && (
                <div className="border border-[#D4AF37]/50 bg-[#D4AF37]/5 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Life Ash</div>
                  <div className="font-display text-lg mt-1">+{reward.reward.life_ash}</div>
                </div>
              )}
              {reward.reward.booster && (
                <div className="border border-[#EC4899]/50 bg-[#EC4899]/5 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#EC4899]">Blueprint Booster</div>
                  <div className="font-display text-lg mt-1">
                    {reward.reward.booster.icon || "📐"} {reward.reward.booster.name}
                  </div>
                </div>
              )}
            </div>
          )}
          <button
            data-testid="arcade-back-to-cabinet"
            onClick={() => { setReward(null); setSelectedHero(null); refreshActive(); }}
            className="mt-6 border-2 border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] px-6 py-2 uppercase tracking-widest text-xs hover:bg-[#D4AF37]/20"
          >
            Back to Cabinet
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="miko-arcade-shell" className="text-white">
      {/* ═══ CABINET MARQUEE ═══ */}
      <div className="border-2 border-[#D4AF37] bg-[#0A0A0A] p-6 mb-6 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: "repeating-linear-gradient(0deg,#38BDF8 0 1px,transparent 1px 4px)",
          }}
        />
        <div className="relative text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#38BDF8]/50 text-[#38BDF8] mb-3">
            <Gamepad2 size={12} />
            <span className="font-accent text-[10px] tracking-[0.3em] uppercase">
              Miko&apos;s Arcade — Pocket-World Cabinet
            </span>
          </div>
          <h3 className="font-display text-3xl md:text-5xl mb-2 leading-none">
            Insert Coin. <span className="text-[#38BDF8]">Step into the pocket world.</span>
          </h3>
          <p className="text-neutral-400 max-w-xl mx-auto text-sm">
            A cabinet that became real — like a book you can step into.
            Deanah &amp; Muki saw her through the glass. The new heroes went in.
            Play for fun. Bring back Miko-upgrade blueprints.
          </p>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-3">
            📖 Read D&amp;M&apos;s account · 🎮 Enter the pocket world · 🦉 Unlock hero Miko + blueprints
          </div>
        </div>
      </div>

      {/* ═══ COIN-OP STATUS STRIP ═══ */}
      <div
        data-testid="miko-arcade-coinop"
        className="flex items-center justify-between border border-zinc-800 bg-[#0F0F0F] px-4 py-3 mb-6"
      >
        <div className="flex items-center gap-3 text-sm flex-wrap">
          <Coins size={16} className="text-[#38BDF8]" />
          <span className="text-neutral-400">Insert:</span>
          <span className="text-[#38BDF8] font-bold" title={coinOp.coin_flavor || ""}>
            {coinOp.cost_per_play ?? "—"} {coinOp.coin_icon || "👻"} {coinOp.coin_label || "Blue Phantom Coin"}
          </span>
          <span className="text-neutral-600">·</span>
          <span className="text-neutral-400">Continues:</span>
          <span className="text-[#F97316] font-bold" title={coinOp.continue_coin_flavor || ""}>
            {coinOp.continues_allowed ?? 0} × {coinOp.continue_coin_icon || "🔥"} {coinOp.continue_coin_label || "Fire Coin"}
          </span>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">
          Daily first play: <span className="text-emerald-400">FREE</span>
        </div>
      </div>
      <div className="mb-6 text-[11px] text-neutral-500 italic px-1">
        Two coins Deanah found on her adventure — the 👻 blue phantom from the Tomb of Awakening opens the cabinet; the 🔥 fire coin buys you a continue.
      </div>

      {/* ═══ HERO PICKER ═══ */}
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">
          Select your fighter · {roster.length} available
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {roster.map((h) => {
            const specs = catalog.specials?.[h.class_id] || [];
            const pending = h.spec_status === "OWNER_DROP_PENDING";
            const locked  = h.unlocked === false;
            const isSel = selectedHero === h.class_id;
            return (
              <button
                key={h.class_id}
                data-testid={`arcade-hero-${h.class_id}`}
                data-locked={locked ? "true" : "false"}
                disabled={locked}
                onClick={() => { if (!locked) setSelectedHero(h.class_id); }}
                title={locked ? (h.unlock_hint || "Locked") : ""}
                className={`text-left border-2 bg-[#0F0F0F] p-4 transition-colors relative ${
                  locked
                    ? "border-zinc-800 opacity-60 cursor-not-allowed"
                    : isSel
                    ? "border-[#D4AF37] bg-[#D4AF37]/5"
                    : "border-zinc-800 hover:border-zinc-600"
                }`}
                style={{ borderTopColor: locked ? "#3f3f46" : h.color, borderTopWidth: 3 }}
              >
                {locked && (
                  <div
                    data-testid={`arcade-hero-locked-${h.class_id}`}
                    className="absolute top-2 right-2 flex items-center gap-1 border border-zinc-700 bg-black/60 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-neutral-400"
                  >
                    <Lock size={9} /> Locked
                  </div>
                )}
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className="w-12 h-12 flex items-center justify-center text-3xl border"
                    style={{
                      backgroundColor: (locked ? "#3f3f46" : h.color) + "22",
                      borderColor:     locked ? "#3f3f46" : h.color,
                      filter:          locked ? "grayscale(0.9)" : "none",
                    }}
                  >
                    {locked ? "🔒" : h.portrait}
                  </div>
                  <div>
                    <div className="font-display text-lg leading-tight">
                      {locked ? "???" : h.display}
                    </div>
                    <div className="text-[10px] tracking-widest uppercase text-neutral-500">
                      {locked ? (h.role || "Hidden Fighter") : h.role}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-neutral-400 mb-3 italic">
                  {locked
                    ? (h.unlock_hint || "Beat the full arcade game once to reveal this fighter.")
                    : h.tagline}
                </p>
                {!locked && (
                  <div className="grid grid-cols-3 gap-1 text-[10px] text-neutral-500 mb-3">
                    <div><span className="text-neutral-300 font-bold">{h.base_hp}</span> HP</div>
                    <div><span className="text-neutral-300 font-bold">{h.base_patk}</span> PATK</div>
                    <div><span className="text-neutral-300 font-bold">{h.base_matk}</span> MATK</div>
                  </div>
                )}
                <div className="border-t border-zinc-800 pt-2">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 flex items-center gap-1">
                    Specials
                    {pending && (
                      <span className="text-amber-400 text-[9px]">· awaiting drop</span>
                    )}
                    {locked && (
                      <span className="text-neutral-500 text-[9px]">· sealed</span>
                    )}
                  </div>
                  {locked ? (
                    <div className="text-[11px] text-neutral-600 italic">
                      Kit sealed until first full clear.
                    </div>
                  ) : specs.length === 0 ? (
                    <div className="text-[11px] text-neutral-600 italic">
                      Owner is authoring this hero&apos;s kit.
                    </div>
                  ) : (
                    specs.map((sp) => (
                      <div key={sp.id} className="text-[11px] text-neutral-300 leading-tight mb-0.5">
                        <span className="text-[#D4AF37]">{sp.slot}</span> · {sp.label}
                        <span className="text-neutral-500 ml-1">({sp.cost_bars}★)</span>
                      </div>
                    ))
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══ STAGES OVERVIEW / AWAITING DROP ═══ */}
      <div className="border border-zinc-800 bg-[#0F0F0F] p-5 mb-6" data-testid="miko-arcade-stages">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
            Cabinet stages · {stages.length} areas
          </div>
          {!stagesReady && (
            <div className="flex items-center gap-1 text-amber-400 text-[10px] uppercase tracking-widest">
              <Lock size={10} /> Awaiting owner drop
            </div>
          )}
        </div>
        {stagesReady ? (
          <div className="space-y-3">
            {stages.map((st) => {
              const isSecret = !!st.is_secret_branch;
              const hasMiko = (st.sub_stages || []).some(
                (s) => s.boss_id === "miko_area5_boss",
              );
              const isFinal = (st.sub_stages || []).some((s) => s.is_final);
              return (
                <div
                  key={st.area_id}
                  data-testid={`arcade-area-${st.area_id}`}
                  className={`border-l-2 pl-3 py-2 ${
                    isFinal
                      ? "border-[#F59E0B] bg-[#F59E0B]/5"
                      : hasMiko
                      ? "border-[#38BDF8] bg-[#38BDF8]/5"
                      : isSecret
                      ? "border-emerald-500 bg-emerald-500/5"
                      : "border-zinc-700"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[#D4AF37] font-bold text-sm">
                      Area {st.area_id}
                    </span>
                    <span className="font-display text-lg">{st.label}</span>
                    {isSecret && (
                      <span className="text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1">
                        🗝️ Secret branch — unlocks via Blueprint Keycard
                      </span>
                    )}
                    {hasMiko && (
                      <span className="text-[10px] uppercase tracking-widest text-[#38BDF8]">
                        🦉 Boss Miko — unlocks playable form
                      </span>
                    )}
                    {isFinal && (
                      <span className="text-[10px] uppercase tracking-widest text-[#F59E0B]">
                        🔴 Final Gauntlet
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-neutral-500 mb-2 italic">
                    {st.environment}
                  </p>
                  <ul className="text-xs text-neutral-300 space-y-0.5">
                    {(st.sub_stages || []).map((sub) => (
                      <li key={sub.id} className="flex items-start gap-2">
                        <span className="text-neutral-500 font-mono min-w-[2.5rem]">
                          {sub.id}
                        </span>
                        <span className={sub.is_boss ? "text-[#D4AF37] font-bold" : ""}>
                          {sub.title}
                          {sub.contains_item && (
                            <span className="text-emerald-400 ml-1 text-[10px]">
                              🗝️
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-neutral-500 text-sm">
            <div className="mb-2 text-lg">🎮 GAME OVER — INSERT COIN</div>
            <div>The cabinet is powered on. Stages, enemies, and boss kits</div>
            <div>are being authored by the owner. Come back for the drop.</div>
          </div>
        )}
      </div>

      {/* ═══ BOSS PREVIEW ═══ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div
          className="border border-zinc-800 bg-[#0F0F0F] p-4"
          data-testid="miko-arcade-final-boss-card"
        >
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
            Final Boss · Area 10-3
          </div>
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-10 h-10 flex items-center justify-center text-2xl border"
              style={{
                backgroundColor: (catalog.master_automaton?.color || "#F59E0B") + "22",
                borderColor: catalog.master_automaton?.color || "#F59E0B",
              }}
            >
              {catalog.master_automaton?.portrait || "🤖"}
            </div>
            <div>
              <div className="font-display text-lg">
                {catalog.master_automaton?.display || "The Master Automaton"}
              </div>
              <div className="text-[10px] italic text-neutral-500">
                {catalog.master_automaton?.tagline}
              </div>
            </div>
          </div>
          {catalog.master_automaton?.spec_status === "OWNER_DROP_PENDING" && (
            <div className="text-amber-400 text-xs flex items-center gap-1">
              <Lock size={10} /> Awaiting owner drop
            </div>
          )}
        </div>
        <div
          className="border border-zinc-800 bg-[#0F0F0F] p-4"
          data-testid="miko-arcade-miko-boss-card"
        >
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
            Area 5-3 Boss · Unlocks Playable
          </div>
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-10 h-10 flex items-center justify-center text-2xl border"
              style={{ backgroundColor: miko.color + "22", borderColor: miko.color }}
            >
              {miko.portrait}
            </div>
            <div>
              <div className="font-display text-lg">{miko.display}</div>
              <div className="text-[10px] italic text-neutral-500">
                {miko.tagline || miko.discovery_hint}
              </div>
            </div>
          </div>
          <div className="text-[11px] text-neutral-400 leading-snug">
            Defeat her at Area 5-3 (The Grand Workshop Core) to un-trap
            her as a playable RPG hero.
          </div>
        </div>
      </div>

      {/* ═══ PLAY BUTTON — LIVE (Aug 2026 owner flip) ═══ */}
      <button
        data-testid="miko-arcade-insert-coin"
        disabled={!selectedHero || inserting}
        onClick={async () => {
          if (!selectedHero) return;
          setInserting(true);
          try {
            const r = await axios.post(
              `${API}/miko-arcade/session/start`,
              { hero_class: selectedHero },
            );
            setActiveSession(r.data);
            setSessionPayload(r.data);   // triggers canvas mount
            toast.success(`Entering the pocket world · ${roster.find((h) => h.class_id === selectedHero)?.display || selectedHero}`);
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Coin rejected — try again.");
          } finally {
            setInserting(false);
          }
        }}
        className={`w-full py-3 border-2 uppercase font-bold tracking-widest text-sm flex items-center justify-center gap-2 transition-colors ${
          !selectedHero
            ? "border-zinc-800 bg-[#0F0F0F] text-neutral-600 cursor-not-allowed"
            : "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 cursor-pointer"
        }`}
      >
        {inserting ? (
          <><Loader2 size={14} className="animate-spin" /> Crossing the glass...</>
        ) : selectedHero ? (
          <><PlayCircle size={14} /> Insert Coin · Enter as {roster.find((h) => h.class_id === selectedHero)?.display}</>
        ) : (
          <><Coins size={14} /> Pick a hero to enter the pocket world</>
        )}
      </button>
      <div className="text-[10px] text-neutral-600 text-center mt-2">
        {coinOp.cost_per_play ?? "—"} {coinOp.coin_icon || "👻"} {coinOp.coin_label || "Blue Phantom Coin"} per play
        {" · "}
        {coinOp.continues_allowed ?? 0} × {coinOp.continue_coin_icon || "🔥"} {coinOp.continue_coin_label || "Fire Coin"} for continues
        {housing.documented_in && (
          <> · 📖 documented in Deanah&apos;s book</>
        )}
      </div>
    </div>
  );
}
